import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Upload,
  X,
  GripVertical,
  Image as ImageIcon,
  Trash2,
  Plus,
  Loader2,
  Check,
  AlertCircle,
  FileText,
  Plane,
  ClipboardList,
  CreditCard,
  Star,
} from 'lucide-react';
import { useAppStore } from '../store';
import { uploadImage, validateImageFile, createImagePreview, revokeImagePreview } from '../services/storage.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import {
  ArrivalDepartureEditor,
  HouseRulesEditor,
  PaymentTermsEditor,
  SeasonalPricingEditor,
} from '@/components/editors';
import type { StructuredPropertyContent, SeasonalPricingConfig } from '../types';

interface ImageItem {
  id: string;
  url: string;
  file?: File;
  isNew: boolean;
  isUploading?: boolean;
  uploadProgress?: number;
}

type ContentTab = 'arrival' | 'rules' | 'payment';

// Default structured content for new properties
const getDefaultStructuredContent = (): StructuredPropertyContent => ({
  arrivalDeparture: {
    checkInTime: '16:00',
    checkOutTime: '11:00',
    keyInstructions: {
      en: 'Key safe is located next to the main entrance. You will receive the code 24 hours before arrival.',
      de: 'Der Schlüsselsafe befindet sich neben dem Haupteingang. Sie erhalten den Code 24 Stunden vor Anreise.',
    },
    parkingInfo: {
      en: 'Free private parking available on-site for up to 2 vehicles.',
      de: 'Kostenloser Privatparkplatz vor Ort für bis zu 2 Fahrzeuge.',
    },
    directionsByCar: {
      en: '',
      de: '',
    },
    directionsByTrain: {
      en: '',
      de: '',
    },
    contactNumber: '+41 79 123 45 67',
  },
  houseRules: {
    groups: [],
  },
  paymentTerms: {
    paymentMethods: [],
    cancellationPolicy: [],
    cleaningFee: 0,
    petFee: 0,
    depositAmount: 500,
    paymentDeadlineDays: 30,
  },
});

const EditPropertyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { homes, updateHome, currentUser } = useAppStore();

  const home = homes.find(h => h.id === id);

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [season, setSeason] = useState<'warm' | 'cold'>('warm');
  const [weekdayRate, setWeekdayRate] = useState(0);
  const [weekendRate, setWeekendRate] = useState(0);
  const [familyRate, setFamilyRate] = useState(0);
  const [seasonalConfig, setSeasonalConfig] = useState<SeasonalPricingConfig | undefined>(undefined);
  const [totalBedrooms, setTotalBedrooms] = useState(0);
  const [totalBeds, setTotalBeds] = useState(0);
  const [totalBathrooms, setTotalBathrooms] = useState(0);
  const [maxGuests, setMaxGuests] = useState(0);

  // Structured content state
  const [structuredContent, setStructuredContent] = useState<StructuredPropertyContent>(
    getDefaultStructuredContent()
  );
  const [activeContentTab, setActiveContentTab] = useState<ContentTab>('arrival');

  // Image state
  const [images, setImages] = useState<ImageItem[]>([]);
  const [featuredImageId, setFeaturedImageId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Only initialize form state once — prevents live Firestore updates from resetting user edits
  const initialized = useRef(false);

  // Load home data
  useEffect(() => {
    if (home && !initialized.current) {
      initialized.current = true;
      setName(home.name);
      setCategory(home.category || '');
      setDescription(home.description);
      setShortDescription(home.shortDescription || '');
      setSeason(home.pricingStructure.season);
      setWeekdayRate(home.pricingStructure.weekdayRate);
      setWeekendRate(home.pricingStructure.weekendRate);
      setFamilyRate(home.pricingStructure.userTypeOverrides?.family || home.pricingStructure.weekdayRate);
      setSeasonalConfig(home.pricingStructure.seasonalConfig);
      setTotalBedrooms(home.layout?.totalBedrooms || 0);
      setTotalBeds(home.layout?.totalBeds || 0);
      setTotalBathrooms(home.layout?.totalBathrooms || 0);
      setMaxGuests(home.layout?.maxGuests || 0);

      // Initialize structured content - use existing or create default
      setStructuredContent(home.structuredContent || getDefaultStructuredContent());

      // Initialize images
      const initialImages = home.images.map((url, index) => ({
        id: `existing-${index}`,
        url,
        isNew: false,
      }));
      setImages(initialImages);
      const idx = Math.min(home.featuredImageIndex ?? 0, Math.max(0, initialImages.length - 1));
      setFeaturedImageId(initialImages[idx]?.id ?? null);
    }
  }, [home]);

  // Track changes
  useEffect(() => {
    if (home) {
      const hasFormChanges =
        name !== home.name ||
        category !== (home.category || '') ||
        description !== home.description ||
        shortDescription !== (home.shortDescription || '') ||
        season !== home.pricingStructure.season ||
        weekdayRate !== home.pricingStructure.weekdayRate ||
        weekendRate !== home.pricingStructure.weekendRate ||
        familyRate !== (home.pricingStructure.userTypeOverrides?.family || home.pricingStructure.weekdayRate) ||
        totalBedrooms !== (home.layout?.totalBedrooms || 0) ||
        totalBeds !== (home.layout?.totalBeds || 0) ||
        totalBathrooms !== (home.layout?.totalBathrooms || 0) ||
        maxGuests !== (home.layout?.maxGuests || 0);

      const hasImageChanges = images.some(img => img.isNew) ||
        images.length !== home.images.length ||
        images.some((img, i) => !img.isNew && img.url !== home.images[i]);

      const currentFeaturedIndex = images.findIndex(img => img.id === featuredImageId);
      const storedFeaturedIndex = Math.min(home.featuredImageIndex ?? 0, Math.max(0, home.images.length - 1));
      const hasFeaturedChange = currentFeaturedIndex >= 0 && currentFeaturedIndex !== storedFeaturedIndex;

      // Check structured content changes (simple deep comparison)
      const originalContent = home.structuredContent || getDefaultStructuredContent();
      const hasContentChanges =
        JSON.stringify(structuredContent) !== JSON.stringify(originalContent);

      setHasChanges(hasFormChanges || hasImageChanges || hasContentChanges || hasFeaturedChange);
    }
  }, [name, category, description, shortDescription, season, weekdayRate, weekendRate, familyRate, totalBedrooms, totalBeds, totalBathrooms, maxGuests, images, featuredImageId, structuredContent, home]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.isNew && img.url.startsWith('blob:')) {
          revokeImagePreview(img.url);
        }
      });
    };
  }, []);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const newImages: ImageItem[] = [];

    Array.from(files).forEach(file => {
      const validation = validateImageFile(file);
      if (validation.valid) {
        const preview = createImagePreview(file);
        newImages.push({
          id: `new-${Date.now()}-${Math.random()}`,
          url: preview,
          file,
          isNew: true,
        });
      } else {
        setError(validation.error || 'Invalid image file');
      }
    });

    if (newImages.length > 0) {
      setImages(prev => {
        const merged = [...prev, ...newImages];
        setFeaturedImageId(currentId => currentId ?? merged[0]?.id ?? null);
        return merged;
      });
      setError(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleRemoveImage = useCallback((index: number) => {
    setImages(prev => {
      const newImages = [...prev];
      const removed = newImages.splice(index, 1)[0];
      if (removed.isNew && removed.url.startsWith('blob:')) {
        revokeImagePreview(removed.url);
      }
      setFeaturedImageId(currentId => {
        if (currentId !== removed.id) return currentId;
        return newImages[0]?.id ?? null;
      });
      return newImages;
    });
  }, []);

  // Drag and drop reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOverItem = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setImages(prev => {
      const newImages = [...prev];
      const draggedItem = newImages[draggedIndex];
      newImages.splice(draggedIndex, 1);
      newImages.splice(index, 0, draggedItem);
      setDraggedIndex(index);
      return newImages;
    });
  };

  const handleSave = async () => {
    if (!home || !name || !description) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Upload new images
      const uploadedUrls: string[] = [];
      const orderedIds: string[] = [];

      for (const img of images) {
        if (img.isNew && img.file) {
          setImages(prev => prev.map(i =>
            i.id === img.id ? { ...i, isUploading: true } : i
          ));

          const uploadedUrl = await uploadImage(img.file, 'holiday-homes', (progress) => {
            setImages(prev => prev.map(i =>
              i.id === img.id ? { ...i, uploadProgress: progress } : i
            ));
          });
          uploadedUrls.push(uploadedUrl);
          orderedIds.push(img.id);

          setImages(prev => prev.map(i =>
            i.id === img.id ? { ...i, url: uploadedUrl, isUploading: false, uploadProgress: undefined, isNew: false, file: undefined } : i
          ));
        } else if (!img.isNew) {
          uploadedUrls.push(img.url);
          orderedIds.push(img.id);
        }
      }

      const featuredIdx = Math.max(0, orderedIds.findIndex(id => id === featuredImageId));

      // Update home
      await updateHome(home.id, {
        name,
        category: category || undefined,
        description,
        shortDescription: shortDescription || undefined,
        images: uploadedUrls,
        featuredImageIndex: featuredIdx,
        pricingStructure: {
          season,
          weekdayRate,
          weekendRate,
          combinedRate: Math.round((weekdayRate + weekendRate) / 2),
          userTypeOverrides: {
            family: familyRate || weekdayRate,
            guest: weekdayRate,
          },
          seasonalConfig,
        },
        layout: totalBedrooms > 0 || totalBeds > 0 ? {
          totalRooms: totalBedrooms,
          totalBedrooms,
          totalBeds,
          totalBathrooms,
          maxGuests,
          rooms: home.layout?.rooms || [],
        } : undefined,
        structuredContent,
      });

      setSaveSuccess(true);
      setHasChanges(false);

      // Reset success message after delay
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving property:', err);
      setError('Failed to save property. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Access check
  if (!currentUser?.isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-16 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You need admin privileges to edit properties.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!home) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-16 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Property Not Found</h2>
            <p className="text-muted-foreground mb-4">The property you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Edit Property</h1>
            <p className="text-muted-foreground">{home.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Badge variant="secondary">Unsaved changes</Badge>
          )}
          {saveSuccess && (
            <Badge variant="default" className="bg-green-600">
              <Check className="h-3 w-3 mr-1" />
              Saved
            </Badge>
          )}
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
          <TabsTrigger value="details" className="gap-2">
            <ImageIcon className="h-4 w-4" />
            Property Details
          </TabsTrigger>
          <TabsTrigger value="content" className="gap-2">
            <FileText className="h-4 w-4" />
            Guest Information
          </TabsTrigger>
        </TabsList>

        {/* Property Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content - Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>The main details about your property</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Property Name *</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter property name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Kategorie <span className="text-muted-foreground text-xs">(z.B. Ferienhaus, Chalet, Bauernhof)</span></Label>
                    <Input
                      id="category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      placeholder="z.B. Ferienhaus"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="shortDescription">Kurzbeschreibung <span className="text-muted-foreground text-xs">(Kartenansicht)</span></Label>
                    <Input
                      id="shortDescription"
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                      placeholder="z.B. Direkt am See, 5 km vom Dorf. Grosser Garten mit Seeblick."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe the property, its features, and surroundings..."
                      className="min-h-[120px]"
                    />
                  </div>

                </CardContent>
              </Card>

              {/* Images */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Property Images
                  </CardTitle>
                  <CardDescription>
                    Bilder hochladen, per Drag &amp; Drop sortieren. Mit dem Stern-Button wählst du das Favoriten-Bild, das auf der Unterkunfts-Karte angezeigt wird.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload Zone */}
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`
                      border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                      ${isDragOver
                        ? 'border-primary bg-primary/5'
                        : 'border-muted-foreground/25 hover:border-primary/50'
                      }
                    `}
                    onClick={() => document.getElementById('image-upload')?.click()}
                  >
                    <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm font-medium mb-1">
                      Drop images here or click to upload
                    </p>
                    <p className="text-xs text-muted-foreground">
                      JPEG, PNG, or WebP up to 5MB each
                    </p>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFileSelect(e.target.files)}
                    />
                  </div>

                  {/* Image Grid */}
                  {images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {images.map((image, index) => {
                        const isFeatured = image.id === featuredImageId;
                        return (
                        <div
                          key={image.id}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOverItem(e, index)}
                          className={`
                            relative group rounded-lg overflow-hidden border-2 aspect-[4/3]
                            ${draggedIndex === index ? 'opacity-50 border-primary' : 'border-transparent'}
                            ${isFeatured ? 'ring-2 ring-yellow-500 ring-offset-2' : ''}
                          `}
                        >
                          <img
                            src={image.url}
                            alt={`Property image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />

                          {/* Overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors">
                            {/* Drag Handle */}
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="bg-white/90 rounded p-1 cursor-grab active:cursor-grabbing">
                                <GripVertical className="h-4 w-4 text-gray-700" />
                              </div>
                            </div>

                            {/* Favorite Button */}
                            <button
                              type="button"
                              onClick={() => setFeaturedImageId(image.id)}
                              aria-label={isFeatured ? 'Favoriten-Bild' : 'Als Favorit setzen'}
                              title={isFeatured ? 'Favoriten-Bild' : 'Als Favorit setzen'}
                              className={`absolute top-2 right-10 rounded-full p-1.5 transition-opacity ${
                                isFeatured
                                  ? 'bg-yellow-500 text-white opacity-100'
                                  : 'bg-white/90 text-gray-700 hover:bg-yellow-500 hover:text-white opacity-0 group-hover:opacity-100'
                              }`}
                            >
                              <Star className={`h-4 w-4 ${isFeatured ? 'fill-current' : ''}`} />
                            </button>

                            {/* Remove Button */}
                            <button
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                            >
                              <X className="h-4 w-4" />
                            </button>

                            {/* Featured Badge */}
                            {isFeatured && (
                              <div className="absolute bottom-2 left-2">
                                <Badge className="bg-yellow-500 text-white">
                                  <Star className="h-3 w-3 mr-1 fill-current" />
                                  Favorit
                                </Badge>
                              </div>
                            )}

                            {/* New Badge */}
                            {image.isNew && (
                              <div className="absolute bottom-2 right-2">
                                <Badge variant="secondary">New</Badge>
                              </div>
                            )}

                            {/* Uploading Overlay */}
                            {image.isUploading && (
                              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
                                <Loader2 className="h-8 w-8 text-white animate-spin" />
                                {image.uploadProgress != null && (
                                  <>
                                    <span className="text-white text-sm font-medium">{image.uploadProgress}%</span>
                                    <div className="w-3/4 h-1.5 bg-white/30 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-white rounded-full transition-all duration-300"
                                        style={{ width: `${image.uploadProgress}%` }}
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}

                      {/* Add More Button */}
                      <button
                        onClick={() => document.getElementById('image-upload')?.click()}
                        className="aspect-[4/3] border-2 border-dashed border-muted-foreground/25 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary/50 hover:bg-muted/50 transition-colors"
                      >
                        <Plus className="h-8 w-8 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Add Image</span>
                      </button>
                    </div>
                  )}

                  {images.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No images uploaded yet. Add at least one image for your property.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Room Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Room Information</CardTitle>
                  <CardDescription>Details about bedrooms, beds, and capacity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bedrooms">Bedrooms</Label>
                      <Input
                        id="bedrooms"
                        type="number"
                        min="0"
                        value={totalBedrooms || ''}
                        onChange={(e) => setTotalBedrooms(parseInt(e.target.value) || 0)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="beds">Beds</Label>
                      <Input
                        id="beds"
                        type="number"
                        min="0"
                        value={totalBeds || ''}
                        onChange={(e) => setTotalBeds(parseInt(e.target.value) || 0)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bathrooms">Bathrooms</Label>
                      <Input
                        id="bathrooms"
                        type="number"
                        min="0"
                        value={totalBathrooms || ''}
                        onChange={(e) => setTotalBathrooms(parseInt(e.target.value) || 0)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxGuests">Max Guests</Label>
                      <Input
                        id="maxGuests"
                        type="number"
                        min="0"
                        value={maxGuests || ''}
                        onChange={(e) => setMaxGuests(parseInt(e.target.value) || 0)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Seasonal Pricing */}
              <Card>
                <CardHeader>
                  <CardTitle>Seasonal Pricing</CardTitle>
                  <CardDescription>
                    Configure pricing by season with high/low season rates for different user types
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <SeasonalPricingEditor
                    config={seasonalConfig}
                    onChange={setSeasonalConfig}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - Right Column */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(`/homes/${home.id}`)}
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    View Public Page
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-destructive hover:text-destructive"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this property? This cannot be undone.')) {
                        navigate('/admin');
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Property
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Guest Information Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Guest Information
              </CardTitle>
              <CardDescription>
                Manage property-specific content for arrival, house rules, and payment terms.
                This information is displayed to guests on the property detail page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeContentTab} onValueChange={(v) => setActiveContentTab(v as ContentTab)}>
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="arrival" className="gap-2">
                    <Plane className="h-4 w-4" />
                    <span className="hidden sm:inline">Arrival & Departure</span>
                    <span className="sm:hidden">Arrival</span>
                  </TabsTrigger>
                  <TabsTrigger value="rules" className="gap-2">
                    <ClipboardList className="h-4 w-4" />
                    <span className="hidden sm:inline">House Rules</span>
                    <span className="sm:hidden">Rules</span>
                  </TabsTrigger>
                  <TabsTrigger value="payment" className="gap-2">
                    <CreditCard className="h-4 w-4" />
                    <span className="hidden sm:inline">Payment Terms</span>
                    <span className="sm:hidden">Payment</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="arrival">
                  <ArrivalDepartureEditor
                    value={structuredContent.arrivalDeparture}
                    onChange={(v) =>
                      setStructuredContent({ ...structuredContent, arrivalDeparture: v })
                    }
                  />
                </TabsContent>

                <TabsContent value="rules">
                  <HouseRulesEditor
                    value={structuredContent.houseRules}
                    onChange={(v) =>
                      setStructuredContent({ ...structuredContent, houseRules: v })
                    }
                  />
                </TabsContent>

                <TabsContent value="payment">
                  <PaymentTermsEditor
                    value={structuredContent.paymentTerms}
                    onChange={(v) =>
                      setStructuredContent({ ...structuredContent, paymentTerms: v })
                    }
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sticky Save Bar for Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t lg:hidden">
        <Button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className="w-full"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EditPropertyPage;
